import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { GET_INSTANCES_BY_IDS_QUERY } from "../../queries/instanceQueries";

export function useInstances(instancesMap) {
  const [instances, setInstances] = useState({});
  const [originalAnnotationsIds, setOriginalAnnotationsIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isRefreshing = useRef(false); // ✅ Add refresh guard

  const [getInstances] = useLazyQuery(GET_INSTANCES_BY_IDS_QUERY, {
    fetchPolicy: "network-only",
  });

  // ✅ Extract fetchInstances as a separate callback for reuse
  const fetchInstances = useCallback(async () => {
    if (
      !instancesMap ||
      Object.keys(instancesMap).length === 0 ||
      isRefreshing.current
    ) {
      if (!instancesMap || Object.keys(instancesMap).length === 0) {
        setInstances({});
        setOriginalAnnotationsIds({});
      }
      return { instances: {}, annotations: {} };
    }

    try {
      isRefreshing.current = true;
      setLoading(true);
      setError(null);

      // Extract instance IDs from the map
      const instanceIds = Object.keys(instancesMap);

      const { data } = await getInstances({
        variables: { ids: instanceIds },
        fetchPolicy: "network-only", // ✅ Force fresh data
      });

      if (data?.instancesByIds) {
        // Create object with field names as keys and instance objects as values
        const instancesByField = {};
        const allAnnotations = {};

        data.instancesByIds.forEach(instance => {
          const fieldName = instancesMap[instance._id];
          if (fieldName) {
            instancesByField[fieldName] = instance;

            // Extract annotations if available
            if (instance.annotations) {
              allAnnotations[instance._id] = instance.annotations;
            }
          }
        });

        setInstances(instancesByField);
        setOriginalAnnotationsIds(allAnnotations);

        // Return the fresh data for immediate use
        return { instances: instancesByField, annotations: allAnnotations };
      }

      return { instances: {}, annotations: {} };
    } catch (err) {
      setError(err);
      return { instances: {}, annotations: {} };
    } finally {
      setLoading(false);
      isRefreshing.current = false;
    }
  }, [instancesMap, getInstances]);

  // ✅ Add refresh function similar to useDigitalTwinData
  const refreshInstances = useCallback(async () => {
    return await fetchInstances();
  }, [fetchInstances]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  return {
    instances,
    originalAnnotationsIds,
    loading,
    error,
    refreshInstances, // ✅ Export refresh function
  };
}
